import { useDraggable } from '@dnd-kit/core';
import { useCallback, useMemo, useState } from 'react';
import { eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek, isToday, isWeekend as fnsIsWeekend, parseISO } from 'date-fns';
import type { CustomField, Task } from '@/lib/schemas';
import type { SprintDef } from '@/hooks/use-calendar';
import { useI18n } from '@/lib/i18n';
import { CalendarDayCell } from './calendar-day-cell';
import { CalendarEventCard } from './calendar-event-card';
import { CalendarContextMenu, type CalendarContextMenuItem } from './calendar-context-menu';
import { colorToHex, resolveCalendarColor, resolveTaskDate, extractIsoDate, type CalendarDateSourceWithAll } from './calendar-config';

// ============================================
// DRAGGABLE EVENT
// ============================================
//
// Thin wrapper that wires CalendarEventCard into dnd-kit's useDraggable.
// Lives here (not in calendar-event-card.tsx) so the presentational
// pill stays reusable from the Agenda view, which has no drag.

interface DraggableEventProps {
    task: Task;
    colorHex: string;
    isStart: boolean;
    isEnd: boolean;
    isSingleDay: boolean;
    onClick?: (task: Task) => void;
}

function DraggableEvent({
    task,
    colorHex,
    isStart,
    isEnd,
    isSingleDay,
    onClick,
}: DraggableEventProps) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `calendar-event-${task.id}`,
        data: { type: 'calendar-event', taskId: task.id },
    });

    const style = isDragging ? { opacity: 0 } : undefined;

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none"
        >
            <CalendarEventCard
                task={task}
                colorHex={colorHex}
                isStart={isStart}
                isEnd={isEnd}
                isSingleDay={isSingleDay}
                onClick={onClick}
            />
        </div>
    );
}

// ============================================
// MONTH VIEW
// ============================================
//
// t16: a 7-column grid that lays out 5–6 rows of CalendarDayCells for
// the currently-visible month. Multi-day tasks render once per day they
// span, with the pill's isStart / isEnd flags derived per-day so the
// borders line up to look like one continuous bar.

export interface CalendarMonthProps {
    /** The anchor day — we render the month that contains this date. */
    anchor: Date;
    tasks: Task[];
    customFields: CustomField[];
    sprints: SprintDef[];
    sprintField: CustomField | null;
    /** T21 R4: user-defined date ranges that render as bands. */
    periods?: import('./calendar-config').PeriodDef[];
    colorBy: string;
    dateSource: CalendarDateSourceWithAll;
    onTaskClick?: (task: Task) => void;
    /** Right-click on a day cell — parent opens the create-task dialog pre-filled with this day. */
    onCreateTaskAtDate?: (dateIso: string) => void;
    /** Right-click on a task pill — parent opens the delete confirmation. */
    onDeleteTask?: (taskId: string) => void;
    canEdit?: boolean;
}

export function CalendarMonth({
    anchor,
    tasks,
    customFields,
    sprints,
    sprintField,
    periods,
    colorBy,
    dateSource,
    onTaskClick,
    onCreateTaskAtDate,
    onDeleteTask,
    canEdit,
}: CalendarMonthProps) {
    const { t } = useI18n();

    // First day of the grid = Monday of the week containing the 1st of
    // the month. Last day = Sunday of the week containing the last day
    // of the month. Using date-fns's startOfWeek (weekStartsOn: 1).
    const gridStart = startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    // Pre-compute which days each task lands on. Stored as a Map<isoDay,
    // Array<{ task, isStart, isEnd, isSingleDay }>> so the month grid
    // just reads from it per cell.
    //
    // t18: the day-key is derived via `extractIsoDate` instead of
    // `new Date(iso).getTime()` so a task with dueDate set to
    // "2026-09-15T00:00:00.000Z" (UTC midnight) always lands on Sept 15
    // in the user's local TZ, not Sept 14 — fixing the day-1 bug.
    const eventsByDay = useMemo(() => {
        const map = new Map<string, Array<{
            task: Task;
            isStart: boolean;
            isEnd: boolean;
            isSingleDay: boolean;
        }>>();
        for (const task of tasks) {
            const resolution = resolveTaskDate(task, dateSource, sprints);
            if (!resolution.start) continue;
            // Slice to YYYY-MM-DD to avoid the UTC→local TZ day shift
            // (fix for the day-1 bug — see calendar-config.ts).
            const startKey = extractIsoDate(resolution.start);
            const endKey = extractIsoDate(resolution.end ?? resolution.start);
            if (!startKey || !endKey) continue;
            const single = startKey === endKey;
            // Re-anchor in local TZ so we can iterate every day in the
            // span without timezone drift. parseISO('YYYY-MM-DD') parses
            // as local midnight, which is what the grid expects.
            const startDay = parseISO(startKey);
            const endDay = parseISO(endKey);
            const clampedStart = startDay < gridStart ? gridStart : startDay;
            const clampedEnd = endDay > gridEnd ? gridEnd : endDay;
            const span = eachDayOfInterval({ start: clampedStart, end: clampedEnd });
            for (const day of span) {
                const key = format(day, 'yyyy-MM-dd');
                const list = map.get(key) ?? [];
                list.push({
                    task,
                    isStart: key === startKey,
                    isEnd: key === endKey,
                    isSingleDay: single,
                });
                map.set(key, list);
            }
        }
        return map;
    }, [tasks, sprints, dateSource, gridStart, gridEnd]);

    // Cap events per cell to 3 visible pills + an "+N more" overflow so
    // busy months stay readable.
    const MAX_VISIBLE = 3;

    // ----- Right-click context menu -----
    //
    // t18: a single shared menu instance anchored at the cursor. Day
    // cells get "Create task at <date>", task pills get "Open task" +
    // "Delete task". The `mode` distinguishes which target fired the
    // event so the menu can render the right items.

    const [menuState, setMenuState] = useState<{
        open: boolean;
        x: number;
        y: number;
        mode: 'day' | 'task';
        dateIso?: string;
        task?: Task;
    }>({ open: false, x: 0, y: 0, mode: 'day' });

    const closeMenu = useCallback(() => {
        setMenuState((s) => ({ ...s, open: false }));
    }, []);

    const openDayMenu = useCallback((dateIso: string, ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        setMenuState({
            open: true,
            x: ev.clientX,
            y: ev.clientY,
            mode: 'day',
            dateIso,
        });
    }, []);

    const openTaskMenu = useCallback((task: Task, ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        setMenuState({
            open: true,
            x: ev.clientX,
            y: ev.clientY,
            mode: 'task',
            task,
        });
    }, []);

    const menuItems = useMemo<CalendarContextMenuItem[]>(() => {
        if (!menuState.open) return [];
        if (menuState.mode === 'day' && menuState.dateIso && onCreateTaskAtDate) {
            const date = menuState.dateIso;
            // Format a friendly label like "Monday, Sep 15" so the user
            // can confirm they're creating on the right day.
            let label = t('calendar.contextMenu.createTaskAt');
            try {
                const friendly = format(parseISO(date), 'EEEE, MMM d');
                label = label.replace('{date}', friendly);
            } catch {
                label = label.replace('{date}', date);
            }
            return [
                {
                    id: 'create-task',
                    label,
                    icon: 'create',
                    onClick: () => onCreateTaskAtDate(date),
                },
            ];
        }
        if (menuState.mode === 'task' && menuState.task) {
            const task = menuState.task;
            const items: CalendarContextMenuItem[] = [];
            if (onTaskClick) {
                items.push({
                    id: 'open-task',
                    label: t('calendar.contextMenu.openTask'),
                    icon: 'open',
                    onClick: () => onTaskClick(task),
                });
            }
            if (onDeleteTask) {
                items.push({
                    id: 'delete-task',
                    label: t('calendar.contextMenu.deleteTask'),
                    icon: 'delete',
                    danger: true,
                    onClick: () => onDeleteTask(task.id),
                });
            }
            return items;
        }
        return [];
    }, [menuState, onCreateTaskAtDate, onTaskClick, onDeleteTask, t]);

    return (
        // T30 R6: overflow-x-auto lets the grid scroll horizontally on
        // very narrow phones (≤360px). min-w-[36rem] is the floor for
        // 7 cells × ~50px each — below that the grid scrolls rather
        // than squeezing the date numbers into illegible 1-2 char
        // columns. The wrapper keeps the border + rounded corners so
        // the scrollbar looks native to the calendar card.
        <div className="rounded-lg border border-white/10 bg-surface/30 overflow-hidden">
            <div className="overflow-x-auto">
            {/* Weekday header */}
            <div className="grid grid-cols-7 min-w-[36rem] border-b border-white/10 bg-surface/50">
                {[1, 2, 3, 4, 5, 6, 0].map((dow) => (
                    <div
                        key={dow}
                        className="px-1 sm:px-2 py-1.5 text-[10px] sm:text-[11px] font-medium uppercase tracking-wider text-foreground-muted"
                    >
                        {t(`calendar.weekday.${dow}` as any)}
                    </div>
                ))}
            </div>
            {/* Day grid */}
            <div className="grid grid-cols-7 min-w-[36rem]">
                {days.map((day) => {
                    const key = format(day, 'yyyy-MM-dd');
                    const events = eventsByDay.get(key) ?? [];
                    const visible = events.slice(0, MAX_VISIBLE);
                    const overflow = Math.max(0, events.length - MAX_VISIBLE);
                    return (
                        <CalendarDayCell
                            key={key}
                            id={`month-day-${key}`}
                            date={day}
                            isCurrentMonth={isSameMonth(day, anchor)}
                            isToday={isToday(day)}
                            isWeekend={fnsIsWeekend(day)}
                            sprints={sprints}
                            periods={periods}
                            className="border-r border-b border-white/10 last:border-r-0"
                            onContextMenu={(e) => openDayMenu(key, e)}
                        >
                            {visible.map(({ task, isStart, isEnd, isSingleDay }) => {
                                const colorHex = colorToHex(
                                    resolveCalendarColor(task, colorBy, sprints, sprintField),
                                    customFields
                                );
                                // Wrap the pill so the right-click event
                                // bubbles up with the task context but
                                // doesn't trigger the cell's day menu.
                                const pill = canEdit !== false ? (
                                    <DraggableEvent
                                        key={`${task.id}-${key}`}
                                        task={task}
                                        colorHex={colorHex}
                                        isStart={isStart}
                                        isEnd={isEnd}
                                        isSingleDay={isSingleDay}
                                        onClick={onTaskClick}
                                    />
                                ) : (
                                    <CalendarEventCard
                                        key={`${task.id}-${key}`}
                                        task={task}
                                        colorHex={colorHex}
                                        isStart={isStart}
                                        isEnd={isEnd}
                                        isSingleDay={isSingleDay}
                                        onClick={onTaskClick}
                                    />
                                );
                                // Wrap each pill in a div that swallows
                                // its own right-click and re-fires the
                                // task menu at the cursor. We do this
                                // outside the dnd-kit wrapper so the
                                // existing drag listeners stay intact.
                                return (
                                    <div
                                        key={`${task.id}-${key}-wrap`}
                                        onContextMenu={(e) => openTaskMenu(task, e)}
                                        data-testid={`calendar-pill-wrap-${task.id}-${key}`}
                                    >
                                        {pill}
                                    </div>
                                );
                            })}
                            {overflow > 0 && (
                                <button
                                    type="button"
                                    className="block w-full text-left text-[10px] text-foreground-muted hover:text-foreground px-1.5"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    +{overflow} {t('calendar.more')}
                                </button>
                            )}
                        </CalendarDayCell>
                    );
                })}
            </div>
            </div>

            <CalendarContextMenu
                open={menuState.open}
                x={menuState.x}
                y={menuState.y}
                items={menuItems}
                onClose={closeMenu}
            />
        </div>
    );
}
